import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { getNextPageIndex, pageRange } from '../../../utils/pagination';
import { toSearchWords } from '../../../utils/searchText';
import { keys } from './keys';
import type { InboxEmail, InboxEmailStatus, SuggestedAction, TopicRecommendation } from '../types/inbox.types';

const INBOX_PAGE_SIZE = 25;

type Params = {
  spaceId?: string;
  status?: InboxEmailStatus;
  search?: string;
};

async function fetchInboxEmails(
  spaceId: string,
  status: InboxEmailStatus | undefined,
  search: string,
  pageIndex: number
): Promise<InboxEmail[]> {
  const { from, to } = pageRange(pageIndex, INBOX_PAGE_SIZE);

  let query = apiClient
    .from('inbox_emails')
    // Every column but `search_text`, which duplicates the body and would double the payload.
    .select(
      'id, space_id, from_email, from_name, to_email, subject, body_text, body_html, received_at, status, matched_sub_topic_id, suggested_actions, document_summary, topic_recommendation, created_at, updated_at, attachments:inbox_attachments(*)'
    )
    .eq('space_id', spaceId)
    .order('received_at', { ascending: false })
    .order('id', { ascending: false })
    .range(from, to);

  if (status) {
    query = query.eq('status', status);
  }

  // `search_text` = subject, sender, body and attachment names, lowercase without accents.
  for (const word of toSearchWords(search)) {
    query = query.ilike('search_text', `%${word}%`);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data || []).map((row) => {
    let parsedActions: SuggestedAction[] = [];
    if (Array.isArray(row.suggested_actions)) {
      parsedActions = row.suggested_actions as unknown as SuggestedAction[];
    }
    let parsedTopicRecommendation: TopicRecommendation | null = null;
    if (row.topic_recommendation && typeof row.topic_recommendation === 'object') {
      parsedTopicRecommendation = row.topic_recommendation as unknown as TopicRecommendation;
    }
    return {
      ...row,
      parsedActions,
      parsedTopicRecommendation,
    };
  });
}

export function useGetInboxEmails(params: Params) {
  const spaceId = params.spaceId;
  const search = params.search?.trim() ?? '';
  const listKey = keys.list(spaceId ?? '', params.status);

  return useInfiniteQuery({
    queryKey: keys.listSearch(spaceId ?? '', params.status, search),
    queryFn: ({ pageParam }) => fetchInboxEmails(spaceId!, params.status, search, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextPageIndex(INBOX_PAGE_SIZE),
    // Keep the current rows while a new search loads, but not across tabs (other status).
    placeholderData: (previousData, previousQuery) =>
      previousQuery && listKey.every((part, index) => previousQuery.queryKey[index] === part) ? previousData : undefined,
    enabled: Boolean(spaceId),
  });
}
